import React, { useState, useEffect, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Polygon, Circle } from '@react-google-maps/api';
import { intelligentSearchService } from '../../services/IntelligentSearchService';
import { leadGenService } from '../../services/leadGeneration';
import { useAuth } from '../../context/AuthContext';
import LocationAutocomplete from './LocationAutocomplete';
import PlaceTypeDropdown from '../common/PlaceTypeDropdown';
import { formatPlaceTypeDisplay } from '../../utils/supportedPlaceTypes';
import ConfirmationModal from '../common/ConfirmationModal';
import ChainDetectionModal from '../search/ChainDetectionModal';
import CircleAnalysisModal from '../search/CircleAnalysisModal';
import toast from 'react-hot-toast';
import Loading from '../common/Loading';
import { GOOGLE_MAPS_CONFIG, mapContainerStyle } from '../../config/googleMapsConfig';
import GuideSystem from '../common/GuideSystem';
import FloatingGuideButton from '../common/FloatingGuideButton';
import { getGuideContent } from '../../utils/guideContent';
import { useGuide } from '../../hooks/useGuide';

const IntelligentSearchPage = () => {
  const { user, getRemainingRequests, getRequestLimit, getUserType } = useAuth();
  const [map, setMap] = useState(null);
  
  // Location selection state
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationBoundary, setLocationBoundary] = useState(null);
  const [showBoundary, setShowBoundary] = useState(false);
  
  // Custom boundary state
  const [boundaryMode, setBoundaryMode] = useState('geocoded'); // 'geocoded', 'pentagon', 'tube'
  const [customShape, setCustomShape] = useState(null);
  
  // Polygon shape state
  const [isDragging, setIsDragging] = useState(false);
  const [shapeSize, setShapeSize] = useState(0.02);
  const [tubeThickness, setTubeThickness] = useState(0.01);
  
  // NEW: Shape interaction mode and undo state
  const [shapeInteractionMode, setShapeInteractionMode] = useState('move'); // 'move' or 'reshape'
  const [shapeHistory, setShapeHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isCtrlPressed, setIsCtrlPressed] = useState(false);
  
  // Shape dragging state
  const [isShapeDragging, setIsShapeDragging] = useState(false);
  const [shapeDragStart, setShapeDragStart] = useState(null);
  
  // Search area and circles state
  const [searchAreas, setSearchAreas] = useState([]);
  const [selectedSearchArea, setSelectedSearchArea] = useState(null);
  const [generatedCircles, setGeneratedCircles] = useState([]);
  const [selectedCircles, setSelectedCircles] = useState(new Set());
  
  // Search configuration state
  const [includedTypes, setIncludedTypes] = useState(() => {
    const saved = localStorage.getItem('intelligent_included_types');
    return saved ? JSON.parse(saved) : ['restaurant'];
  });
  const [excludedTypes, setExcludedTypes] = useState(() => {
    const saved = localStorage.getItem('intelligent_excluded_types');
    return saved ? JSON.parse(saved) : [];
  });
  const [useAiOptimization, setUseAiOptimization] = useState(true);
  
  // UI state
  const [currentStep, setCurrentStep] = useState('location'); // 'location', 'area', 'circles', 'search', 'results'
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
 
 // Search results state
 const [searchResults, setSearchResults] = useState([]);
 const [lastSearchSession, setLastSearchSession] = useState(null);
 const [circleAnalysis, setCircleAnalysis] = useState(null);
 const [chainDetection, setChainDetection] = useState(null);
 const [showAnalysisModal, setShowAnalysisModal] = useState(false);
 const [showChainModal, setShowChainModal] = useState(false);

 const { isGuideVisible, hasSeenGuide, showGuide, hideGuide } = useGuide('intelligentSearch');
  const guideData = getGuideContent('intelligentSearch');
 
 // Map state
 const [mapCenter, setMapCenter] = useState({ lat: 40.7128, lng: -74.0060 });
 
 // Previously searched shapes state
 const [searchedShapes, setSearchedShapes] = useState([]);
 
 // Confirmation modal state
 const [confirmationModal, setConfirmationModal] = useState({
   isOpen: false,
   title: '',
   message: '',
   onConfirm: () => {},
   type: 'default',
   confirmText: 'OK',
   cancelText: 'Cancel'
 });

 const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_CONFIG);

 const searchResultsRef = useRef(null);

 // NEW: Save shape state to history
 const saveShapeToHistory = (shape) => {
   if (!shape) return;
   
   const newHistory = shapeHistory.slice(0, historyIndex + 1);
   newHistory.push(JSON.parse(JSON.stringify(shape)));
   
   // Limit history to 20 items
   if (newHistory.length > 20) {
     newHistory.shift();
   } else {
     setHistoryIndex(prev => prev + 1);
   }
   
   setShapeHistory(newHistory);
 };

 // NEW: Undo functionality
 const undoShapeChange = () => {
   if (historyIndex > 0) {
     setHistoryIndex(prev => prev - 1);
     setCustomShape(JSON.parse(JSON.stringify(shapeHistory[historyIndex - 1])));
     toast.success('Shape change undone');
   } else {
     toast('No more undo steps available', {
    icon: 'ℹ️',
    duration: 2000
  });
   }
 };

 // NEW: Initialize shape history when shape is created
 useEffect(() => {
   if (customShape && shapeHistory.length === 0) {
     setShapeHistory([JSON.parse(JSON.stringify(customShape))]);
     setHistoryIndex(0);
   }
 }, [customShape?.center]);

 // NEW: Keyboard event handlers
 useEffect(() => {
  const handleKeyDown = (event) => {
    if (event.ctrlKey) {
      setIsCtrlPressed(true);
      if (event.key === 'z' || event.key === 'Z') {
        event.preventDefault();
        undoShapeChange();
      }
    }
    
    if (event.key === 'm' || event.key === 'M') {
      setShapeInteractionMode(prev => prev === 'move' ? 'reshape' : 'move');
      toast(`Shape interaction mode: ${shapeInteractionMode === 'move' ? 'Reshape' : 'Move'}`, {
        icon: 'ℹ️',
        duration: 2000
      });
    }
  }; // <- This closing brace was missing

  const handleKeyUp = (event) => {
    if (!event.ctrlKey) {
      setIsCtrlPressed(false);
    }
  };

  window.addEventListener('keydown', handleKeyDown);
  window.addEventListener('keyup', handleKeyUp);

  return () => {
    window.removeEventListener('keydown', handleKeyDown);
    window.removeEventListener('keyup', handleKeyUp);
  };
}, [shapeInteractionMode, historyIndex]);

 // Polygon shape creation functions
 const createPentagonPoints = (center, radius) => {
   const lat = center.lat;
   const lng = center.lng;
   const points = [];
   
   for (let i = 0; i < 5; i++) {
     const angle = (i * 72 - 90) * (Math.PI / 180);
     const pointLat = lat + radius * Math.cos(angle);
     const pointLng = lng + radius * Math.sin(angle) / Math.cos(lat * Math.PI / 180);
     points.push({ lat: pointLat, lng: pointLng });
   }
   
   return points;
 };

 const updateShapeSize = (newSize) => {
   if (!customShape || !customShape.center) return;
   
   const center = customShape.center;
   const outerPoints = createPentagonPoints(center, newSize);
   
   let updatedShape;
   
   if (boundaryMode === 'pentagon') {
     updatedShape = {
       ...customShape,
       outerPoints,
       outerRadius: newSize
     };
   } else if (boundaryMode === 'tube') {
     const innerRadius = Math.max(0.002, newSize - tubeThickness);
     const innerPoints = createPentagonPoints(center, innerRadius);
     updatedShape = {
       ...customShape,
       outerPoints,
       innerPoints,
       outerRadius: newSize,
       innerRadius
     };
   }
   
   setCustomShape(updatedShape);
   saveShapeToHistory(updatedShape);
 };

 const updateTubeThickness = (newThickness) => {
   if (!customShape || boundaryMode !== 'tube' || !customShape.center) return;
   
   const center = customShape.center;
   const outerRadius = customShape.outerRadius;
   const innerRadius = Math.max(0.002, outerRadius - newThickness);
   const innerPoints = createPentagonPoints(center, innerRadius);
   
   const updatedShape = {
     ...customShape,
     innerPoints,
     thickness: newThickness,
     innerRadius
   };
   
   setCustomShape(updatedShape);
   saveShapeToHistory(updatedShape);
 };

 // UPDATED: Handle shape dragging with mode awareness
 const handleShapeDragStart = (event) => {
   if (!customShape || shapeInteractionMode !== 'move') return;
   
   setIsShapeDragging(true);
   setShapeDragStart({
     lat: event.latLng.lat(),
     lng: event.latLng.lng(),
     shapeCenter: customShape.center
   });
 };

 const handleShapeDrag = (event) => {
   if (!isShapeDragging || !shapeDragStart || !customShape || shapeInteractionMode !== 'move') return;
   
   const currentLat = event.latLng.lat();
   const currentLng = event.latLng.lng();
   
   const latOffset = currentLat - shapeDragStart.lat;
   const lngOffset = currentLng - shapeDragStart.lng;
   
   const newCenter = {
     lat: shapeDragStart.shapeCenter.lat + latOffset,
     lng: shapeDragStart.shapeCenter.lng + lngOffset
   };
   
   const outerPoints = createPentagonPoints(newCenter, customShape.outerRadius || shapeSize);
   
   let updatedShape = {
     ...customShape,
     center: newCenter,
     outerPoints
   };
   
   if (boundaryMode === 'tube' && customShape.innerPoints) {
     const innerRadius = customShape.innerRadius || (customShape.outerRadius - tubeThickness);
     const innerPoints = createPentagonPoints(newCenter, innerRadius);
     updatedShape.innerPoints = innerPoints;
   }
   
   setCustomShape(updatedShape);
 };

 const handleShapeDragEnd = () => {
   if (isShapeDragging && customShape) {
     saveShapeToHistory(customShape);
   }
   setIsShapeDragging(false);
   setShapeDragStart(null);
 };

 // UPDATED: Handle corner dragging with mode awareness
 const handleCornerDrag = (event, cornerIndex, isInner = false) => {
   if (!customShape || shapeInteractionMode !== 'reshape') return;
   
   const newLat = event.latLng.lat();
   const newLng = event.latLng.lng();
   
   let updatedShape = { ...customShape };
   
   if (isInner && boundaryMode === 'tube') {
     const newInnerPoints = [...customShape.innerPoints];
     newInnerPoints[cornerIndex] = { lat: newLat, lng: newLng };
     updatedShape.innerPoints = newInnerPoints;
   } else {
     const newOuterPoints = [...customShape.outerPoints];
     newOuterPoints[cornerIndex] = { lat: newLat, lng: newLng };
     updatedShape.outerPoints = newOuterPoints;
   }
   
   setCustomShape(updatedShape);
 };

 // UPDATED: Handle corner drag end to save history
 const handleCornerDragEnd = () => {
   if (customShape) {
     saveShapeToHistory(customShape);
   }
   setIsDragging(false);
 };

 // Update shape size when slider changes
 useEffect(() => {
   if (customShape && customShape.center) {
     updateShapeSize(shapeSize);
   }
 }, [shapeSize]);

 // Update tube thickness when slider changes
 useEffect(() => {
   if (customShape && boundaryMode === 'tube' && customShape.center) {
     updateTubeThickness(tubeThickness);
   }
 }, [tubeThickness]);

 // Load existing search areas on mount
 useEffect(() => {
   loadSearchAreas();
   loadSearchedShapes();
 }, []);

 // Function to load previously searched shapes
 const loadSearchedShapes = async () => {
  try {
    console.log('Loading searched shapes...');
    const { data, error } = await leadGenService.getSearchShapes();
    if (data) {
      // Combine both searched and unsearched shapes
      const shapes = [
        ...data.searched_shapes?.areas?.filter(area => area.shape_data) || [],
        ...data.unsearched_shapes?.areas?.filter(area => area.shape_data) || []
      ];
      
      // console.log('Loaded searched shapes:', shapes);
      setSearchedShapes(shapes);
    } else if (error) {
      console.error('Error loading searched shapes:', error);
    }
  } catch (error) {
    console.error('Error loading searched shapes:', error);
  }
};

 // Handle custom shape creation
 const handleShapeCreate = (center) => {
   let newShape;
   
   if (boundaryMode === 'pentagon') {
     const outerPoints = createPentagonPoints(center, shapeSize);
     newShape = {
       type: 'pentagon',
       center,
       outerPoints,
       outerRadius: shapeSize
     };
   } else if (boundaryMode === 'tube') {
     const outerPoints = createPentagonPoints(center, shapeSize);
     const innerPoints = createPentagonPoints(center, shapeSize - tubeThickness);
     newShape = {
       type: 'tube',
       center,
       outerPoints,
       innerPoints,
       outerRadius: shapeSize,
       thickness: tubeThickness
     };
   }
   
   setCustomShape(newShape);
   setShapeHistory([JSON.parse(JSON.stringify(newShape))]);
   setHistoryIndex(0);
   toast.success(`${boundaryMode === 'pentagon' ? 'Pentagon' : 'Tube'} boundary created! Press 'M' to toggle between Move/Reshape modes.`);
 };

 // Handle custom shape deletion
 const handleShapeDelete = () => {
   setCustomShape(null);
   setBoundaryMode('geocoded');
   setShapeHistory([]);
   setHistoryIndex(-1);
 };

 // Helper function to show confirmation modal
 const showConfirmation = ({ title, message, onConfirm, type = 'default', confirmText = 'OK', cancelText = 'Cancel' }) => {
   setConfirmationModal({
     isOpen: true,
     title,
     message,
     onConfirm,
     type,
     confirmText,
     cancelText
   });
 };

 const closeConfirmation = () => {
   setConfirmationModal({
     isOpen: false,
     title: '',
     message: '',
     onConfirm: () => {},
     type: 'default',
     confirmText: 'OK',
     cancelText: 'Cancel'
   });
 };

 const loadSearchAreas = async () => {
   const { data, error } = await intelligentSearchService.getIntelligentSearchAreas();
   if (data) {
     setSearchAreas(data.search_areas || []);
   } else if (error) {
     console.error('Error loading search areas:', error);
   }
 };

 const handleLocationSelect = async (prediction) => {
   setIsLoading(true);
   
   const { data, error } = await intelligentSearchService.geocodeLocation(prediction.description);
   
   if (data && data.location) {
     setSelectedLocation(data.location);
     setLocationBoundary(data.location.boundary_coordinates);
     setShowBoundary(true);
     setBoundaryMode('geocoded'); // Switch back to geocoded boundary
     
     // Check if we have valid center coordinates
     const centerPoint = data.location.center_point;
     if (centerPoint && centerPoint.latitude !== 0 && centerPoint.longitude !== 0 && map) {
       const center = {
         lat: centerPoint.latitude,
         lng: centerPoint.longitude
       };
       
       // Update map center state first
       setMapCenter(center);
       
       // Pan to center
       map.panTo(center);
       
       // Fit bounds if we have boundary coordinates
       if (data.location.boundary_coordinates && data.location.boundary_coordinates.length > 0) {
         setTimeout(() => {
           const bounds = new window.google.maps.LatLngBounds();
           
           data.location.boundary_coordinates.forEach(coord => {
             if (coord.latitude !== 0 && coord.longitude !== 0) {
               bounds.extend(new window.google.maps.LatLng(coord.latitude, coord.longitude));
             }
           });
           
           if (!bounds.isEmpty()) {
             map.fitBounds(bounds);
           } else {
             map.setZoom(10);
           }
         }, 100);
       } else {
         map.setZoom(10);
       }
     } else {
       console.error('Invalid center coordinates:', centerPoint);
       toast.error('Invalid location coordinates received');
     }
     
     setCurrentStep('area');
     toast.success(`Selected ${data.location.display_name}${data.cached ? ' (cached)' : ''}`);
   } else if (error) {
     toast.error('Failed to geocode location');
     console.error('Geocoding error:', error);
   }
   
   setIsLoading(false);
 };

 const handleMapClick = (event) => {
   // Don't handle map clicks when dragging shapes
   if (isShapeDragging) return;
   
   if ((boundaryMode === 'pentagon' || boundaryMode === 'tube') && currentStep === 'location') {
     const center = { lat: event.latLng.lat(), lng: event.latLng.lng() };
     handleShapeCreate(center);
   }
 };

 // Create search area with shape data
 const handleCreateSearchArea = async () => {
   if (!selectedLocation && boundaryMode === 'geocoded') {
     toast.error('Please select a location or create a custom boundary first');
     return;
   }

   if ((boundaryMode === 'pentagon' || boundaryMode === 'tube') && !customShape) {
     toast.error('Please draw a custom boundary first');
     return;
   }

   setIsLoading(true);

   let areaData;
   
   if (boundaryMode === 'pentagon' || boundaryMode === 'tube') {
     // Use custom boundary
     let boundaryCoords;
     
     if (boundaryMode === 'pentagon') {
       boundaryCoords = customShape.outerPoints.map(point => ({
         latitude: point.lat,
         longitude: point.lng
       }));
     } else if (boundaryMode === 'tube') {
       // For tube shapes, we'll use the outer boundary for the search area
       // The inner boundary will be handled during circle generation
       boundaryCoords = customShape.outerPoints.map(point => ({
         latitude: point.lat,
         longitude: point.lng
       }));
     }
     
     areaData = {
       area_name: `Custom ${boundaryMode === 'pentagon' ? 'Pentagon' : 'Tube'} Area ${new Date().toLocaleDateString()}`,
       boundary_coordinates: boundaryCoords,
       center_point: {
         latitude: customShape.center.lat,
         longitude: customShape.center.lng
       },
       included_types: includedTypes,
       excluded_types: excludedTypes,
       use_ai_optimization: useAiOptimization,
       custom_shape_data: boundaryMode === 'tube' ? {
         shape_type: 'tube',
         inner_boundary: customShape.innerPoints.map(point => ({
           latitude: point.lat,
           longitude: point.lng
         }))
       } : {
         shape_type: 'pentagon'
       }
     };
   } else {
     // Use geocoded location
     areaData = {
       area_name: selectedLocation.formatted_address,
       google_place_id: selectedLocation.place_id,
       boundary_coordinates: locationBoundary,
       center_point: selectedLocation.center_point,
       included_types: includedTypes,
       excluded_types: excludedTypes,
       use_ai_optimization: useAiOptimization
     };
   }

   const { data, error } = await intelligentSearchService.createIntelligentSearchArea(areaData);

   if (data) {
     setSelectedSearchArea(data);
     setSearchAreas(prev => [data, ...prev]);
     setCurrentStep('circles');
     toast.success('Search area created! Generating optimized circles...');
     
     // Wait a moment for circles to be generated, then load them
     setTimeout(() => {
       loadSearchAreaDetails(data.id);
     }, 2000);
   } else if (error) {
     toast.error('Failed to create search area');
     console.error('Error creating search area:', error);
   }

   setIsLoading(false);
 };

 const loadSearchAreaDetails = async (areaId) => {
   const { data, error } = await intelligentSearchService.getIntelligentSearchArea(areaId);
   
   if (data) {
     setSelectedSearchArea(data);
     setGeneratedCircles(data.optimized_circles || []);
     
     if (data.status === 'ready' && data.optimized_circles?.length > 0) {
       setCurrentStep('circles');
       toast.success(`Generated ${data.optimized_circles.length} optimized circles`);
     } else if (data.status === 'processing') {
       toast.info('Still generating circles... Please wait.');
       // Retry loading after a delay
       setTimeout(() => {
         loadSearchAreaDetails(areaId);
       }, 3000);
     } else if (data.status === 'failed') {
       toast.error('Circle generation failed');
     }
   } else if (error) {
     console.error('Error loading search area details:', error);
   }
 };

 const handleCircleSelection = (circleId, isSelected) => {
   const newSelected = new Set(selectedCircles);
   if (isSelected) {
     newSelected.add(circleId);
   } else {
     newSelected.delete(circleId);
   }
   setSelectedCircles(newSelected);
 };

 const selectAllCircles = () => {
   const allCircleIds = new Set(generatedCircles.map(circle => circle.id));
   setSelectedCircles(allCircleIds);
 };

 const deselectAllCircles = () => {
   setSelectedCircles(new Set());
 };

 const handleRegenerateCircles = async () => {
   if (!selectedSearchArea) return;

   showConfirmation({
     title: 'Regenerate Circles',
     message: 'This will delete existing circles and generate new ones. Continue?',
     type: 'warning',
     confirmText: 'Regenerate',
     onConfirm: async () => {
       setIsLoading(true);
       
       const { data, error } = await intelligentSearchService.regenerateCircles(selectedSearchArea.id);
       
       if (data) {
         setGeneratedCircles([]);
         setSelectedCircles(new Set());
         toast.success('Regenerating circles...');
         
         // Wait and reload
         setTimeout(() => {
           loadSearchAreaDetails(selectedSearchArea.id);
         }, 3000);
       } else if (error) {
         toast.error('Failed to regenerate circles');
         console.error('Error regenerating circles:', error);
       }
       
       setIsLoading(false);
     }
   });
 };

 // Execute search with shape saving
 const handleExecuteSearch = async () => {
   if (selectedCircles.size === 0) {
     toast.error('Please select at least one circle to search');
     return;
   }

   const remaining = getRemainingRequests();
   const circlesCount = selectedCircles.size;
   
   if (remaining < circlesCount) {
     toast.error(`Not enough API requests remaining. You need ${circlesCount} requests but only have ${remaining} remaining.`);
     return;
   }

   setIsSearching(true);
   setSearchResults([]);
   setCircleAnalysis(null);
   setChainDetection(null);
   setCurrentStep('search');

   const selectedCircleObjects = generatedCircles.filter(circle => selectedCircles.has(circle.id));

   const { data, error } = await intelligentSearchService.executeIntelligentSearch(
     selectedSearchArea,
     selectedCircleObjects
   );

   if (data) {
     setSearchResults(data.places || []);
     setLastSearchSession(data.search_session);
     
     // Handle searched shape response for intelligent search
     if (data.searched_shape) {
       // Add the searched shape to our list and mark it as searched
       setSearchedShapes(prev => [
         ...prev.filter(shape => shape.id !== data.searched_shape.id),
         {
           ...data.searched_shape,
           has_been_searched: true,
           last_searched_at: new Date().toISOString()
         }
       ]);
       
       // Clear current shape since it's now been searched
       setCustomShape(null);
       setBoundaryMode('geocoded');
       setShapeHistory([]);
       setHistoryIndex(-1);
     }
     
     // Handle circle analysis
     if (data.circle_analysis) {
       setCircleAnalysis(data.circle_analysis);
       
       if (data.circle_analysis.circles_at_limit > 0) {
         toast(data.circle_analysis.message, {
           duration: 6000,
           icon: '⚠️',
           style: {
             background: '#fef3c7',
             color: '#92400e',
             border: '1px solid #fbbf24',
           },
         });
       }
     }

     // Handle chain detection
     if (data.chain_detection) {
       setChainDetection(data.chain_detection);
       
       if (data.chain_detection.unique_chain_count > 0) {
         toast(`🔗 Found ${data.chain_detection.unique_chain_count} chains with ${data.chain_detection.total_chain_places} locations`, {
           duration: 5000,
           style: {
             background: '#dbeafe',
             color: '#1e40af',
             border: '1px solid #3b82f6',
           },
         });
       }
     }
     
     // Update search area status
     if (selectedSearchArea) {
       setSelectedSearchArea(prev => ({
         ...prev,
         status: 'completed'
       }));
     }
     
     setCurrentStep('results');
     toast.success(`Found ${data.places?.length || 0} places`);
     
     // Scroll to results
     setTimeout(() => {
       searchResultsRef.current?.scrollIntoView({ 
         behavior: 'smooth',
         block: 'start'
       });
     }, 100);
   } else if (error) {
     toast.error(error.message || 'Search failed');
     setCurrentStep('circles');
   }

   setIsSearching(false);
 };

 const handleSavePlaces = async () => {
   if (searchResults.length === 0) {
     toast.error('No places to save');
     return;
   }

   setIsSaving(true);

   const { data, error } = await leadGenService.savePlaces(
     searchResults,
     lastSearchSession?.id
   );

   if (data) {
     toast.success(`Saved places! ${data.total_existing > 0 ? `(${data.total_existing} already existed)` : ''}`);
     // Clear search results after saving
     setSearchResults([]);
     setLastSearchSession(null);
     setCurrentStep('circles');
   } else if (error) {
     toast.error('Failed to save places');
     console.error('Error saving places:', error);
   }

   setIsSaving(false);
 };

 const handlePlaceCleanup = async (placeIds, exclusionPatterns) => {
   // Remove places from search results (they're not saved yet)
   setSearchResults(prev => prev.filter(place => !placeIds.includes(place.id)));
   
   if (exclusionPatterns.length > 0) {
     const { data, error } = await leadGenService.addExclusionPatterns(exclusionPatterns);
     
     if (data) {
       toast.success(`${placeIds.length} places removed${data.total_added > 0 ? ` and ${data.total_added} exclusion patterns added` : ''}`);
     } else if (error) {
       toast.error('Failed to add exclusion patterns');
     }
   } else {
     toast.success(`${placeIds.length} places removed`);
   }
 };

 const addIncludedType = (type) => {
   if (!includedTypes.includes(type)) {
     setIncludedTypes(prev => {
       const newTypes = [...prev, type];
       localStorage.setItem('intelligent_included_types', JSON.stringify(newTypes));
       return newTypes;
     });
   }
 };

 const removeIncludedType = (index) => {
   setIncludedTypes(prev => {
     const newTypes = prev.filter((_, i) => i !== index);
     localStorage.setItem('intelligent_included_types', JSON.stringify(newTypes));
     return newTypes;
   });
 };

 const addExcludedType = (type) => {
   if (!excludedTypes.includes(type)) {
     setExcludedTypes(prev => {
       const newTypes = [...prev, type];
       localStorage.setItem('intelligent_excluded_types', JSON.stringify(newTypes));
       return newTypes;
     });
   }
 };

 const removeExcludedType = (index) => {
   setExcludedTypes(prev => {
     const newTypes = prev.filter((_, i) => i !== index);
     localStorage.setItem('intelligent_excluded_types', JSON.stringify(newTypes));
     return newTypes;
   });
 };

 const resetToLocationSelection = () => {
   setCurrentStep('location');
   setSelectedLocation(null);
   setLocationBoundary(null);
   setShowBoundary(false);
   setCustomShape(null);
   setBoundaryMode('geocoded');
   setSelectedSearchArea(null);
   setGeneratedCircles([]);
   setSelectedCircles(new Set());
   setSearchResults([]);
   setShapeHistory([]);
   setHistoryIndex(-1);
 };

 const goBackToCircles = () => {
   setCurrentStep('circles');
   setSearchResults([]);
   setLastSearchSession(null);
 };

 if (!isLoaded) return <Loading />;

 return (
   <div className="h-screen flex flex-col">
    {/* Guide System */}
{guideData && (
  <GuideSystem
    steps={guideData.steps}
    pageName={guideData.pageName}
    isVisible={isGuideVisible}
    onClose={hideGuide}
  />
)}

{/* Floating Guide Button */}
<FloatingGuideButton 
  onClick={showGuide}
  hasNewFeature={!hasSeenGuide}
/>
     {/* Confirmation Modal */}
     <ConfirmationModal
       isOpen={confirmationModal.isOpen}
       onClose={closeConfirmation}
       onConfirm={confirmationModal.onConfirm}
       title={confirmationModal.title}
       message={confirmationModal.message}
       type={confirmationModal.type}
       confirmText={confirmationModal.confirmText}
       cancelText={confirmationModal.cancelText}
     />

     {/* Circle Analysis Modal */}
     {showAnalysisModal && circleAnalysis && (
       <CircleAnalysisModal 
         analysis={circleAnalysis} 
         onClose={() => setShowAnalysisModal(false)} 
       />
     )}

     {/* Chain Detection Modal */}
     {showChainModal && chainDetection && (
       <ChainDetectionModal 
         chainDetection={chainDetection}
         searchResults={searchResults}
         onCleanup={handlePlaceCleanup}
         onClose={() => setShowChainModal(false)} 
       />
     )}

     {/* Header */}
     <div className="bg-white border-b border-gray-200 px-4 py-3 h-[8vh]">
       <div className="flex items-center justify-between">
         <div>
           <h2 className="text-lg font-medium text-gray-900">Intelligent Search</h2>
           <p className="text-sm text-gray-500">AI-powered location optimization for efficient business discovery</p>
         </div>
         
         {/* Progress Indicator */}
         <div className="flex items-center space-x-2">
           <div className={`w-3 h-3 rounded-full ${currentStep === 'location' ? 'bg-blue-500' : currentStep === 'area' || currentStep === 'circles' || currentStep === 'search' || currentStep === 'results' ? 'bg-green-500' : 'bg-gray-300'}`}></div>
           <div className={`w-3 h-3 rounded-full ${currentStep === 'area' ? 'bg-blue-500' : currentStep === 'circles' || currentStep === 'search' || currentStep === 'results' ? 'bg-green-500' : 'bg-gray-300'}`}></div>
           <div className={`w-3 h-3 rounded-full ${currentStep === 'circles' ? 'bg-blue-500' : currentStep === 'search' || currentStep === 'results' ? 'bg-green-500' : 'bg-gray-300'}`}></div>
           <div className={`w-3 h-3 rounded-full ${currentStep === 'search' ? 'bg-blue-500' : currentStep === 'results' ? 'bg-green-500' : 'bg-gray-300'}`}></div>
           <div className={`w-3 h-3 rounded-full ${currentStep === 'results' ? 'bg-green-500' : 'bg-gray-300'}`}></div>
         </div>
       </div>
     </div>

     <div className="flex-1 flex h-[92vh]">
       {/* Sidebar - Fixed height and proper scrolling */}
       <div className="w-80 bg-white border-r border-gray-200 flex flex-col h-full">
         {/* Shape Interaction Mode Display */}
         {(boundaryMode === 'pentagon' || boundaryMode === 'tube') && customShape && (
           <div className="px-4 py-2 bg-blue-50 border-b border-blue-200">
             <div className="flex items-center justify-between">
               <div className="text-sm">
                 <span className="text-blue-700 font-medium">
                   Mode: {shapeInteractionMode === 'move' ? '🔄 Move' : '✏️ Reshape'}
                 </span>
               </div>
               <button
                 onClick={() => {
  setShapeInteractionMode(prev => prev === 'move' ? 'reshape' : 'move');
  toast(`Shape interaction mode: ${shapeInteractionMode === 'move' ? 'Reshape' : 'Move'}`, {
    icon: 'ℹ️',
    duration: 2000
  });
}}
                 className="px-2 py-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 rounded"
               >
                 Toggle (M)
               </button>
             </div>
             <div className="text-xs text-blue-600 mt-1">
               {shapeInteractionMode === 'move' 
                 ? 'Click and drag shape to move it'
                 : 'Drag corners to reshape'
               } • Ctrl+Z to undo
             </div>
           </div>
         )}

         {/* Step 1: Location Selection */}
         {currentStep === 'location' && (
           <div className="flex-1 p-4 overflow-y-auto">
             <h3 className="text-lg font-medium text-gray-900 mb-4">Step 1: Select Location</h3>
             <p className="text-sm text-gray-600 mb-6">
               Search for a city, region, or area to analyze for business opportunities, or draw a custom boundary.
             </p>
             
             <div className="space-y-4">
               {/* Location Autocomplete */}
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-2">
                   Search Location
                 </label>
                 <LocationAutocomplete
                   placeholder="Enter location to search..."
                   onSelect={handleLocationSelect}
                   disabled={isLoading}
                 />
               </div>
               
               {/* OR Divider */}
               <div className="relative">
                 <div className="absolute inset-0 flex items-center">
                   <div className="w-full border-t border-gray-300" />
                 </div>
                 <div className="relative flex justify-center text-sm">
                   <span className="px-2 bg-white text-gray-500">OR</span>
                 </div>
               </div>
               
               {/* Custom Boundary Mode Toggle */}
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-2">
                   Draw Custom Boundary
                 </label>
                 <div className="flex bg-gray-100 rounded-lg p-1 mb-3">
                   <button
                     onClick={() => setBoundaryMode('pentagon')}
                     className={`flex-1 px-2 py-1 text-xs rounded-md transition-colors ${
                       boundaryMode === 'pentagon' 
                         ? 'bg-white text-gray-900 shadow-sm' 
                         : 'text-gray-600 hover:text-gray-900'
                     }`}
                   >
                     Pentagon
                   </button>
                   <button
                     onClick={() => setBoundaryMode('tube')}
                     className={`flex-1 px-2 py-1 text-xs rounded-md transition-colors ${
                       boundaryMode === 'tube' 
                         ? 'bg-white text-gray-900 shadow-sm' 
                         : 'text-gray-600 hover:text-gray-900'
                     }`}
                   >
                     Tube
                   </button>
                 </div>
                 
                 {/* Custom boundary controls */}
                 {(boundaryMode === 'pentagon' || boundaryMode === 'tube') && (
                   <div className="space-y-3">
                     {!customShape ? (
                       <div>
                         <button
                           onClick={() => {
                             toast(`Click on map to draw ${boundaryMode === 'pentagon' ? 'pentagon' : 'tube shape'}`, {
                               icon: 'ℹ️',
                               duration: 3000
                             });
                           }}
                           disabled={isLoading}
                           className="w-full px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 text-sm"
                         >
                           {`Draw ${boundaryMode === 'pentagon' ? 'Pentagon' : 'Tube Shape'}`}
                         </button>
                         <p className="text-xs text-gray-500 mt-2">
                           Click anywhere on the map to create your custom boundary
                         </p>
                       </div>
                     ) : (
                       <div className="space-y-3">
                         {/* Shape Movement Instructions */}
                         <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                           <p className="text-xs text-blue-700">
                             💡 <strong>Controls:</strong><br/>
                             • Press 'M' to toggle Move/Reshape modes<br/>
                             • Ctrl+Z to undo shape changes<br/>
                             • {shapeInteractionMode === 'move' ? 'Drag to move shape' : 'Drag corners to reshape'}
                           </p>
                         </div>

                         {/* Size Control */}
                         <div>
                           <label className="block text-sm font-medium text-gray-700 mb-2">
                             {boundaryMode === 'pentagon' ? 'Pentagon' : 'Outer'} Size
                           </label>
                           <input
                             type="range"
                             min="0.005"
                             max="0.1"
                             step="0.002"
                             value={shapeSize}
                             onChange={(e) => setShapeSize(parseFloat(e.target.value))}
                             className="w-full"
                           />
                           <div className="text-sm text-gray-500 mt-1 text-center">
                             ~{Math.round(shapeSize * 111000)}m radius
                           </div>
                         </div>

                         {/* Tube Thickness Control */}
                         {boundaryMode === 'tube' && (
                           <div>
                             <label className="block text-sm font-medium text-gray-700 mb-2">
                               Tube Thickness
                             </label>
                             <input
                               type="range"
                               min="0.002"
                               max={Math.min(0.05, shapeSize - 0.002)}
                               step="0.001"
                               value={tubeThickness}
                               onChange={(e) => setTubeThickness(parseFloat(e.target.value))}
                               className="w-full"
                             />
                             <div className="text-sm text-gray-500 mt-1 text-center">
                               ~{Math.round(tubeThickness * 111000)}m thick
                             </div>
                           </div>
                         )}

                         {/* Undo Button */}
                         <button
                           onClick={undoShapeChange}
                           disabled={historyIndex <= 0}
                           className="w-full px-3 py-2 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-md hover:bg-yellow-100 disabled:bg-gray-100 disabled:text-gray-400 text-sm"
                         >
                           ↶ Undo Change (Ctrl+Z)
                         </button>

                         {/* Clear Button */}
                         <button
                           onClick={handleShapeDelete}
                           className="w-full px-3 py-2 bg-red-50 border border-red-200 text-red-700 rounded-md hover:bg-red-100 text-sm"
                         >
                           Clear {boundaryMode === 'pentagon' ? 'Pentagon' : 'Tube'}
                         </button>
                       </div>
                     )}
                   </div>
                 )}
               </div>
             </div>

             {customShape && (
               <div className="mt-6 pt-4 border-t border-gray-200">
                 <button
                   onClick={() => setCurrentStep('area')}
                   className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                 >
                   Continue with Custom Boundary →
                 </button>
               </div>
             )}              

             {isLoading && (
               <div className="mt-4 flex items-center text-sm text-gray-600">
                 <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                 Processing location...
               </div>
             )}
           </div>
         )}

         {/* Step 2: Area Configuration */}
         {currentStep === 'area' && (selectedLocation || customShape) && (
           <div className="flex-1 flex flex-col">
             <div className="p-4 border-b border-gray-200">
               <h3 className="text-lg font-medium text-gray-900 mb-2">Step 2: Configure Search Area</h3>
               <div className="text-sm text-gray-600 mb-4">
                 {boundaryMode === 'geocoded' 
                   ? `Selected: ${selectedLocation?.formatted_address}`
                   : `Using custom ${boundaryMode} boundary`
                 }
               </div>
               
               <button
                 onClick={resetToLocationSelection}
                 className="text-sm text-blue-600 hover:text-blue-800 mb-4"
               >
                 ← Choose Different Location
               </button>
             </div>
             
             <div className="flex-1 overflow-y-auto p-4">
               {/* AI Optimization Toggle */}
               <div className="mb-6">
                 <div className="flex items-center">
                   <input
                     id="ai-optimization"
                     type="checkbox"
                     checked={useAiOptimization}
                     onChange={(e) => setUseAiOptimization(e.target.checked)}
                     className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                   />
                   <label htmlFor="ai-optimization" className="ml-3 text-sm font-medium text-gray-700">
                     Enable AI Optimization
                   </label>
                 </div>
                 <p className="mt-1 text-xs text-gray-500">
                   Use population density data to optimize circle placement
                 </p>
               </div>

               {/* Business Types */}
               <div className="mb-6">
                 <h4 className="text-sm font-medium text-gray-700 mb-3">Include Types</h4>
                 <div className="flex flex-wrap gap-2 mb-3">
                   {includedTypes.map((type, index) => (
                     <span key={index} className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                       {formatPlaceTypeDisplay(type)}
                       <button
                         onClick={() => removeIncludedType(index)}
                         className="ml-1 text-blue-600 hover:text-blue-800"
                       >
                         ×
                       </button>
                     </span>
                   ))}
                 </div>
                 <PlaceTypeDropdown
                   placeholder="Add business types..."
                   onAdd={addIncludedType}
                   existingTypes={includedTypes}
                 />
               </div>

               <div className="mb-6">
                 <h4 className="text-sm font-medium text-gray-700 mb-3">Exclude Types</h4>
                 <div className="flex flex-wrap gap-2 mb-3">
                   {excludedTypes.map((type, index) => (
                     <span key={index} className="inline-flex items-center px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                       {formatPlaceTypeDisplay(type)}
                       <button
                         onClick={() => removeExcludedType(index)}
                         className="ml-1 text-red-600 hover:text-red-800"
                       >
                         ×
                       </button>
                     </span>
                   ))}
                 </div>
                 <PlaceTypeDropdown
                   placeholder="Add types to exclude..."
                   onAdd={addExcludedType}
                   existingTypes={excludedTypes}
                 />
               </div>
             </div>

             <div className="p-4 border-t border-gray-200">
               <button
                 onClick={handleCreateSearchArea}
                 disabled={isLoading || includedTypes.length === 0}
                 className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
               >
                 {isLoading ? 'Creating Area...' : 'Create Search Area & Generate Circles'}
               </button>
             </div>
           </div>
         )}

         {/* Step 3: Review Generated Circles */}
         {currentStep === 'circles' && selectedSearchArea && (
           <div className="flex-1 flex flex-col h-full">
             <div className="p-4 border-b border-gray-200 flex-shrink-0">
               <h3 className="text-lg font-medium text-gray-900 mb-2">Step 3: Review Generated Circles</h3>
               <div className="text-sm text-gray-600 mb-4">
                 {selectedSearchArea.area_name}
               </div>
               <button
                 onClick={resetToLocationSelection}
                 className="text-sm text-blue-600 hover:text-blue-800 mb-4"
               >
                 ← Start Over
               </button>

               {generatedCircles.length > 0 && (
                 <div className="mb-4">
                   <div className="text-sm text-gray-600 mb-2">
                     {generatedCircles.length} circles generated | {selectedCircles.size} selected
                   </div>
                   <div className="flex gap-2">
                     <button
                       onClick={selectAllCircles}
                       className="px-3 py-1 text-xs bg-blue-50 border border-blue-200 text-blue-700 rounded hover:bg-blue-100"
                     >
                       Select All
                     </button>
                     <button
                       onClick={deselectAllCircles}
                       className="px-3 py-1 text-xs bg-gray-50 border border-gray-200 text-gray-700 rounded hover:bg-gray-100"
                     >
                       Deselect All
                     </button>
                     <button
                       onClick={handleRegenerateCircles}
                       className="px-3 py-1 text-xs bg-orange-50 border border-orange-200 text-orange-700 rounded hover:bg-orange-100"
                     >
                       Regenerate
                     </button>
                   </div>
                 </div>
               )}
             </div>

             {/* Scrollable circles list */}
             <div className="flex-1 overflow-y-auto p-4">
               {generatedCircles.length === 0 ? (
                 <div className="text-center py-8">
                   <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
                   <p className="text-sm text-gray-600">Generating optimized circles...</p>
                 </div>
               ) : (
                 <div className="space-y-3">
                   {generatedCircles.map((circle) => (
                     <div key={circle.id} className="border border-gray-200 rounded-lg p-3">
                       <div className="flex items-center justify-between">
                         <div className="flex items-center">
                           <input
                             type="checkbox"
                             checked={selectedCircles.has(circle.id)}
                             onChange={(e) => handleCircleSelection(circle.id, e.target.checked)}
                             className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                           />
                           <div>
                             <div className="text-sm font-medium text-gray-900">
                               Circle ({circle.grid_x}, {circle.grid_y})
                             </div>
                             <div className="text-xs text-gray-500">
                               Radius: {intelligentSearchService.formatRadius ? intelligentSearchService.formatRadius(circle.radius) : `${circle.radius}m`}
                             </div>
                           </div>
                         </div>
                         <div className="text-right">
                           {/* {circle.confidence_score && (
                             <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800`}>
                               {circle.confidence_score ? (circle.confidence_score * 100).toFixed(0) + '%' : 'N/A'}
                             </div>
                           )} */}
                           {circle.predicted_density && (
                             <div className="text-xs text-gray-500 mt-1">
                               Density: {circle.predicted_density.toFixed(1)}
                             </div>
                           )}
                         </div>
                       </div>
                     </div>
                   ))}
                 </div>
               )}
             </div>

             {/* Fixed search button at bottom */}
             {generatedCircles.length > 0 && (
               <div className="p-4 border-t border-gray-200 flex-shrink-0">
                 <button
                   onClick={handleExecuteSearch}
                   disabled={selectedCircles.size === 0 || getRemainingRequests() < selectedCircles.size}
                   className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                 >
                   Execute Search on {selectedCircles.size} Circles
                 </button>
                 {selectedCircles.size > getRemainingRequests() && (
                   <div className="text-xs text-red-500 mt-2 text-center">
                     Not enough API requests! Need {selectedCircles.size} but only have {getRemainingRequests()} remaining.
                   </div>
                 )}
                 <div className="text-xs text-gray-500 mt-2 text-center">
                   API Requests remaining: {getRemainingRequests()} / {getRequestLimit()}
                 </div>
               </div>
             )}
           </div>
         )}

         {/* Step 4: Search Progress */}
         {currentStep === 'search' && (
           <div className="flex-1 flex items-center justify-center p-4">
             <div className="text-center">
               <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
               <h3 className="text-lg font-medium text-gray-900 mb-2">Searching...</h3>
               <p className="text-sm text-gray-600">
                 Executing search on {selectedCircles.size} optimized circles
               </p>
             </div>
           </div>
         )}

         {/* Step 5: Results Management */}
         {currentStep === 'results' && (
           <div className="flex-1 flex flex-col h-full">
             <div className="p-4 border-b border-gray-200 flex-shrink-0">
               <h3 className="text-lg font-medium text-gray-900 mb-2">Search Complete</h3>
               <div className="text-sm text-gray-600 mb-4">
                 Found {searchResults.length} places
               </div>
               
               <div className="flex gap-2">
                 <button
                   onClick={goBackToCircles}
                   className="text-sm text-blue-600 hover:text-blue-800"
                 >
                   ← Back to Circles
                 </button>
                 <button
                   onClick={resetToLocationSelection}
                   className="text-sm text-gray-600 hover:text-gray-800"
                 >
                   Start New Search
                 </button>
               </div>
             </div>

             <div className="flex-1 p-4">
               {/* Analysis Buttons */}
               <div className="mb-4 space-y-2">
                 {circleAnalysis && circleAnalysis.circles_at_limit > 0 && (
                   <button
                     onClick={() => setShowAnalysisModal(true)}
                     className="w-full px-3 py-2 bg-orange-50 border border-orange-200 text-orange-700 rounded-md hover:bg-orange-100 text-sm"
                   >
                     ⚠️ Circle Analysis ({circleAnalysis.circles_at_limit} at limit)
                   </button>
                 )}
                 
                 {chainDetection && chainDetection.unique_chain_count > 0 && (
                   <button
                     onClick={() => setShowChainModal(true)}
                     className="w-full px-3 py-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-md hover:bg-blue-100 text-sm"
                   >
                     🔗 Chain Detection ({chainDetection.unique_chain_count} chains)
                   </button>
                 )}
               </div>

               {/* Save Button */}
               <button
                 onClick={handleSavePlaces}
                 disabled={isSaving || searchResults.length === 0}
                 className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
               >
                 {isSaving ? (
                   <>
                     <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                     Saving...
                   </>
                 ) : (
                   `💾 Save ${searchResults.length} Places`
                 )}
               </button>
             </div>
           </div>
         )}

       </div>

       {/* Main Content */}
       <div className="flex-1 flex flex-col">
         {/* Map with zoom controls */}
         <div className="flex-1 relative">
           <GoogleMap
             mapContainerStyle={mapContainerStyle}
             center={mapCenter}
             zoom={12}
             onLoad={setMap}
             onClick={handleMapClick}
             options={{
               streetViewControl: false,
               mapTypeControl: false,
               fullscreenControl: false,
               gestureHandling: 'greedy',
               zoomControl: false,
             }}
           >
             {/* Location Boundary */}
             {showBoundary && locationBoundary && boundaryMode === 'geocoded' && (
               <Polygon
                 paths={locationBoundary.map(coord => ({
                   lat: coord.latitude,
                   lng: coord.longitude
                 }))}
                 options={{
                   fillColor: '#3b82f6',
                   fillOpacity: 0.1,
                   strokeColor: '#3b82f6',
                   strokeOpacity: 0.8,
                   strokeWeight: 2,
                 }}
               />
             )}

             {/* Previously Searched Shapes */}
             {searchedShapes.map((searchedShape) => {
               if (!searchedShape.shape_data) return null;
               
               const isSearched = searchedShape.has_been_searched;
               const shapeType = searchedShape.shape_type === 'pentagon' ? 'polygon' : 'tube';
               
               return (
                 <React.Fragment key={searchedShape.id}>
                   {/* Outer Polygon */}
                   <Polygon
                     paths={searchedShape.shape_data.outerPoints}
                     options={{
                       fillColor: isSearched ? '#6b7280' : (shapeType === 'polygon' ? '#8b5cf6' : '#f59e0b'),
                       fillOpacity: isSearched ? 0.1 : (shapeType === 'polygon' ? 0.2 : 0.15),
                       strokeColor: isSearched ? '#6b7280' : (shapeType === 'polygon' ? '#8b5cf6' : '#f59e0b'),
                       strokeOpacity: isSearched ? 0.4 : 0.8,
                       strokeWeight: isSearched ? 1 : 3,
                       clickable: !(boundaryMode === 'pentagon' || boundaryMode === 'tube')
                     }}
                     onClick={() => {
                       if (isSearched && !(boundaryMode === 'pentagon' || boundaryMode === 'tube')) {
                         toast(`This ${shapeType} area was searched on ${new Date(searchedShape.last_searched_at).toLocaleDateString()}`, {
                           icon: 'ℹ️',
                           duration: 4000,
                           style: {
                             background: '#e0f2fe',
                             color: '#0c4a6e',
                             border: '1px solid #0ea5e9',
                           },
                         });
                       }
                     }}
                   />
                   
                   {/* Inner Polygon (for tube shapes) */}
                   {shapeType === 'tube' && searchedShape.shape_data.innerPoints && (
                     <Polygon
                       paths={searchedShape.shape_data.innerPoints}
                       options={{
                         fillColor: '#6b7280',
                         fillOpacity: 0.1,
                         strokeColor: '#000000',
                         strokeOpacity: 0.8,
                         strokeWeight: 2,
                         clickable: !(boundaryMode === 'pentagon' || boundaryMode === 'tube')
                       }}
                     />
                   )}
                 </React.Fragment>
               );
             })}

             {/* Custom Boundary (Active Drawing) - NOW WITH MODE-AWARE DRAGGABLE FUNCTIONALITY */}
             {customShape && (boundaryMode === 'pentagon' || boundaryMode === 'tube') && (
               <>
                 {/* Outer Pentagon - draggable only in move mode */}
                 <Polygon
                   paths={customShape.outerPoints}
                   options={{
                     fillColor: boundaryMode === 'pentagon' ? '#8b5cf6' : '#f59e0b',
                     fillOpacity: boundaryMode === 'pentagon' ? 0.2 : 0.15,
                     strokeColor: boundaryMode === 'pentagon' ? '#8b5cf6' : '#f59e0b',
                     strokeOpacity: 0.8,
                     strokeWeight: 3,
                     draggable: shapeInteractionMode === 'move',
                     cursor: shapeInteractionMode === 'move' 
                       ? (isShapeDragging ? 'grabbing' : 'grab') 
                       : 'default',
                   }}
                   onDragStart={handleShapeDragStart}
                   onDrag={handleShapeDrag}
                   onDragEnd={handleShapeDragEnd}
                 />
                 
                 {/* Inner Pentagon (for tube shape) - also draggable only in move mode */}
                 {boundaryMode === 'tube' && customShape.innerPoints && (
                   <Polygon
                     paths={customShape.innerPoints}
                     options={{
                       fillColor: '#ffffff',
                       fillOpacity: 1,
                       strokeColor: '#f59e0b',
                       strokeOpacity: 0.8,
                       strokeWeight: 2,
                       draggable: shapeInteractionMode === 'move',
                       cursor: shapeInteractionMode === 'move' 
                         ? (isShapeDragging ? 'grabbing' : 'grab') 
                         : 'default',
                     }}
                     onDragStart={handleShapeDragStart}
                     onDrag={handleShapeDrag}
                     onDragEnd={handleShapeDragEnd}
                   />
                 )}
                 
                 {/* Draggable corner markers for outer pentagon - only visible in reshape mode */}
                 {shapeInteractionMode === 'reshape' && customShape.outerPoints?.map((point, index) => (
                   <Circle
                     key={`outer-${index}`}
                     center={point}
                     radius={150}
                     options={{
                       fillColor: boundaryMode === 'pentagon' ? '#8b5cf6' : '#f59e0b',
                       fillOpacity: 0.8,
                       strokeColor: '#ffffff',
                       strokeOpacity: 1,
                       strokeWeight: 3,
                       clickable: true,
                       draggable: !isLoading,
                       cursor: isLoading ? 'default' : 'move',
                     }}
                     onDragStart={() => setIsDragging(true)}
                     onDrag={() => setIsDragging(true)}
                     onDragEnd={(event) => {
                       handleCornerDrag(event, index, false);
                       handleCornerDragEnd();
                     }}
                   />
                 ))}
                 
                 {/* Draggable corner markers for inner pentagon (tube only) - only visible in reshape mode */}
                 {shapeInteractionMode === 'reshape' && boundaryMode === 'tube' && customShape.innerPoints?.map((point, index) => (
                   <Circle
                     key={`inner-${index}`}
                     center={point}
                     radius={100}
                     options={{
                       fillColor: '#ef4444',
                       fillOpacity: 0.8,
                       strokeColor: '#ffffff',
                       strokeOpacity: 1,
                       strokeWeight: 2,
                       clickable: true,
                       draggable: !isLoading,
                       cursor: isLoading ? 'default' : 'move',
                     }}
                     onDragStart={() => setIsDragging(true)}
                     onDrag={() => setIsDragging(true)}
                     onDragEnd={(event) => {
                       handleCornerDrag(event, index, true);
                       handleCornerDragEnd();
                     }}
                   />
                 ))}
               </>
             )}

             {/* Generated Circles */}
             {generatedCircles.map((circle) => (
               <Circle
                 key={circle.id}
                 center={{
                   lat: circle.center_point.coordinates[1],
                   lng: circle.center_point.coordinates[0],
                 }}
                 radius={circle.radius}
                 options={{
                   fillColor: selectedCircles.has(circle.id) 
                     ? '#10b981' 
                     : circle.has_been_searched 
                       ? '#6b7280' 
                       : '#3b82f6',
                   fillOpacity: selectedCircles.has(circle.id) ? 0.3 : 0.2,
                   strokeColor: selectedCircles.has(circle.id) 
                     ? '#10b981' 
                     : circle.has_been_searched 
                       ? '#6b7280' 
                       : '#3b82f6',
                   strokeOpacity: 0.8,
                   strokeWeight: 2,
                   clickable: !(boundaryMode === 'pentagon' || boundaryMode === 'tube') && !isShapeDragging,
                 }}
                 onClick={() => {
                   if (!(boundaryMode === 'pentagon' || boundaryMode === 'tube') && !isShapeDragging) {
                     const isSelected = selectedCircles.has(circle.id);
                     handleCircleSelection(circle.id, !isSelected);
                   }
                 }}
               />
             ))}
           </GoogleMap>

           {/* Shape Legend */}
           {searchedShapes.length > 0 && (
             <div className="absolute top-4 right-4 bg-white p-3 rounded-lg shadow-lg border">
               <h4 className="text-sm font-medium text-gray-900 mb-2">Shape Legend</h4>
               <div className="space-y-1 text-xs">
                 <div className="flex items-center">
                   <div className="w-4 h-4 bg-purple-500 opacity-20 border border-purple-500 rounded mr-2"></div>
                   <span>Active Pentagon</span>
                 </div>
                 <div className="flex items-center">
                   <div className="w-4 h-4 bg-orange-500 opacity-15 border border-orange-500 rounded mr-2"></div>
                   <span>Active Tube</span>
                 </div>
                 <div className="flex items-center">
                   <div className="w-4 h-4 bg-blue-500 opacity-10 border border-blue-500 rounded mr-2"></div>
                   <span>Geocoded Area</span>
                 </div>
                 <div className="flex items-center">
                   <div className="w-4 h-4 bg-gray-500 opacity-10 border border-gray-500 rounded mr-2"></div>
                   <span>Previously Searched</span>
                 </div>
               </div>
               {searchedShapes.filter(s => s.has_been_searched).length > 0 && (
                 <div className="mt-2 text-xs text-gray-600">
                   {searchedShapes.filter(s => s.has_been_searched).length} searched areas
                 </div>
               )}
             </div>
           )}
         </div>

         {/* Search Results - Fixed height scrollable section */}
         {searchResults.length > 0 && (
           <div className="h-96 bg-white border-t border-gray-200 flex flex-col">
             <div className="p-4 border-b border-gray-200 flex-shrink-0">
               <div className="flex items-center justify-between">
                 <h3 className="text-lg font-medium text-gray-900">
                   Search Results ({searchResults.length})
                 </h3>
               </div>
             </div>
             <div className="flex-1 overflow-y-auto p-4">
               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                 {searchResults.map((place) => (
                   <div key={place.id} className="border border-gray-200 rounded-lg p-4 relative">
                     {/* Remove button */}
                     <button
                       onClick={() => handlePlaceCleanup([place.id], [])}
                       className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                       title="Remove this place"
                     >
                       <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                       </svg>
                     </button>

                     <h4 className="font-medium text-gray-900 mb-2 pr-6">{place.display_name}</h4>
                     <div className="space-y-1 text-sm text-gray-600">
                       {place.formatted_address && (
                         <p>📍 {place.formatted_address}</p>
                       )}
                       {place.international_phone_number && (
                         <p>📞 {place.international_phone_number}</p>
                       )}
                       {place.website_uri && (
                         <p>🌐 <a href={place.website_uri} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Website</a></p>
                       )}
                       {place.rating && (
                         <p>⭐ {place.rating} ({place.user_rating_count} reviews)</p>
                       )}
                     </div>
                   </div>
                 ))}
               </div>
             </div>
           </div>
         )}
       </div>
     </div>
   </div>
 );
};

export default IntelligentSearchPage;